/* eslint-disable no-nested-ternary */
/* eslint-disable no-return-assign */
import { useContext } from 'react';
import { useSelector } from 'react-redux';

import { getAvailableTimeSpansForTreatment } from '../../../actions/areas/planner';
import { TimelineContext, contextDefaultProps } from './TimelineContext';
import { refreshTimeInSeconds } from '../../../settings/scheduler';
import { isTodaysDate } from '../../../utils/dates';
import _ from '../../../utils/lodash';

let liveTrackingIntervalFunc = null;

const useTimeline = () => {
  const [state, setState] = useContext(TimelineContext);
  const isLiveTracking = useSelector(store => store.areas.dailyPlanning.moreOptions.liveTracking);
  const selectedDate = useSelector(store => store.areas.dailyPlanning.selectedDate);

  if (state === null || state.auth === undefined) return {};

  const activeTreatment = state.activeClinician.activeTreatment;
  const activeUnassignedTreatment = state.activeClinician.activeUnassignedTreatment;

  const createRequestModel = baseModel => {
    const modelClone = baseModel ? _.cloneDeep(baseModel) : {};
    modelClone.SelectedDate = useSelector(s => s.areas.dailyPlanning.selectedDate);
    modelClone.IsNightMode = useSelector(s => s.areas.dailyPlanning.scheduler.isNightMode);
    modelClone.OrgUnitId = useSelector(s => s.areas.dailyPlanning.filters.selectedTeam);

    return modelClone;
  };

  const setActiveClinician = (clinicianId, postingId) => {
    const newClinicianIdValue = state.activeClinician.id === clinicianId ? null : clinicianId;
    const newPostingIdValue = state.activeClinician.postingId === postingId ? null : postingId;

    setState({
      ...state,
      activeClinician: { ...state.activeClinician, id: newClinicianIdValue, postingId: newPostingIdValue },
    });
  };
  const setActiveClinicianTreatments = treatments =>
    setState({
      ...state,
      activeClinician: { ...state.activeClinician, treatments, activeTreatment: null },
      isEditMode: false,
    });

  const setActiveTreatment = treatment =>
    setState(newState => ({
      ...newState,
      activeClinician: { ...newState.activeClinician, activeTreatment: treatment },
    }));

  const setActiveAssignment = assignment =>
    setState(newState => ({
      ...newState,
      activeAssignment: assignment,
      availableTimeSpans: contextDefaultProps.availableTimeSpans,
      assignmentForEdit: contextDefaultProps.assignmentForEdit,
      activeTreatmentForCopy: contextDefaultProps.activeTreatmentForCopy,
    }));

  const setActiveUnassignedTreatment = treatment =>
    setState({
      ...state,
      activeClinician: {
        ...state.activeClinician,
        activeTreatment: contextDefaultProps.activeClinician.activeTreatment,
        activeUnassignedTreatment: treatment,
      },
    });

  const hasActiveTreatmentAvailableTimeSpans = () =>
    activeTreatment !== null && activeTreatment.AvailableTimeSpans !== null;
  const hasAvailableTimeSpansForClinicianId = (clinicianId, postingId) =>
    state.activeClinician.id === clinicianId &&
    state.activeClinician.postingId === postingId &&
    state.flags.showClinicianTreatments; // hasActiveTreatmentAvailableTimeSpans();

  const handleFlags = (flagProp, value, disableFlags) => {
    const flags = { ...state.flags, [flagProp]: value };
    if (Array.isArray(disableFlags)) {
      disableFlags.map(f => (flags[f] = false));
    }
    return flags;
  };

  const setFlag = (flagProp, value, disableFlags) =>
    setState({ ...state, flags: handleFlags(flagProp, value, disableFlags) });

  const toggleFlag = flagProp => setFlag(flagProp, !state.flags[flagProp]);

  const setFlagAndClinician = (flagProp, value, clinicianId, postingId, disableFlags, clinician = {}) => {
    const flags = handleFlags(flagProp, value, disableFlags);
    if (value) {
      setState({
        ...state,
        flags,
        activeClinician: {
          clinician,
          id: clinicianId,
          postingId,
          treatments: [],
          activeTreatment: null,
          activeUnassignedTreatment: null,
        },
        availableTimeSpans: contextDefaultProps.availableTimeSpans,
        activeAssignment: contextDefaultProps.activeAssignment,
        assignmentForEdit: contextDefaultProps.assignmentForEdit,
        multiSelectedTreatments: contextDefaultProps.multiSelectedTreatments,
      });
    } else {
      setState({
        ...state,
        flags,
        activeClinician: contextDefaultProps.activeClinician,
        multiSelectedTreatments: contextDefaultProps.multiSelectedTreatments,
      });
    }
  };

  const getParamsForAssignment = () => {
    const returnModel = { treatmentId: null, treatmentInstanceId: null };
    const isValid = state.activeClinician.id !== 0 && state.activeClinician.activeTreatment.treatmentId > 0;

    if (isValid) {
      returnModel.treatmentId = state.activeClinician.activeTreatment.treatmentId;
      returnModel.treatmentInstanceId =
        state.activeClinician.activeTreatment.treatmentInstanceId != null
          ? state.activeClinician.activeTreatment.treatmentInstanceId
          : -1;
    }

    return returnModel;
  };

  const setAvailableTimeSpans = (timeSpans, assignment) =>
    setState({ ...state, availableTimeSpans: timeSpans, assignmentForEdit: assignment });

  const getAvailableTimeSpans = () => {
    return state.availableTimeSpans && state.availableTimeSpans.length > 0
      ? state.availableTimeSpans
      : hasActiveTreatmentAvailableTimeSpans()
      ? activeTreatment.AvailableTimeSpans
      : [];
  };

  const resetContext = newAssignment => {
    setState({
      ...state,
      flags: {
        ...contextDefaultProps.flags,
        isEditMode: state.flags.isEditMode,
      },
      availableTimeSpans: contextDefaultProps.availableTimeSpans,
      activeClinician: contextDefaultProps.activeClinician,
      activeAssignment: newAssignment || contextDefaultProps.activeAssignment,
      assignmentForEdit: contextDefaultProps.assignmentForEdit,
      activeTreatmentForCopy: contextDefaultProps.activeTreatmentForCopy,
      multiSelectedTreatments: contextDefaultProps.multiSelectedTreatments,
      activeNonPatientAssignment: contextDefaultProps.activeNonPatientAssignment,
      activeNonPatientAssignmentForReschedule: contextDefaultProps.activeNonPatientAssignmentForReschedule,
    });
  };

  const resetContextAndModify = modifiedObject => {
    const mergedObject = _.merge({}, _.cloneDeep(contextDefaultProps), modifiedObject);

    setState(mergedObject);
  };

  const setActiveTreatmentInstanceForCopy = treatmentInstance => {
    setState({ ...state, activeTreatmentInstanceForCopy: treatmentInstance });
  };

  const setActiveUnassignedTreatmentForCancel = treatmentInstance => {
    setState({ ...state, activeUnassignedTreatmentForCancel: treatmentInstance });
  };

  const setActiveUnassignedTreatmentForReschedule = treatmentInstance => {
    setState({ ...state, activeUnassignedTreatmentForReschedule: treatmentInstance });
  };

  const setActiveUnassignedTreatmentForChangeUnit = treatmentInstance => {
    setState({ ...state, activeUnassignedTreatmentForChangeUnit: treatmentInstance });
  };

  const refreshClinicianTreatmentsAndClosePanels = () => {
    setState({
      ...state,
      activeClinician: { ...state.activeClinician, activeUnassignedTreatment: null, activeTreatment: null },
      activeUnassignedTreatmentForCancel: null,
      activeUnassignedTreatmentForReschedule: null,
      activeUnassignedTreatmentForChangeUnit: null,
      activeTreatmentInstanceForCopy: null,
      flags: {
        ...state.flags,
        shouldReloadClinicianTreatments: toggleFlag('shouldReloadClinicianTreatments'),
        showTreatmentNotesSidePanel: false,
        showConvertVisitSidePanel: false,
      },
    });
  };

  const toggleEditMode = () =>
    setState(newState => ({
      ...newState,
      availableTimeSpans: contextDefaultProps.availableTimeSpans,
      activeClinician: contextDefaultProps.activeClinician,
      activeAssignment: contextDefaultProps.activeAssignment,
      assignmentForEdit: contextDefaultProps.assignmentForEdit,
      flags: handleFlags('isEditMode', !newState.flags.isEditMode, [
        'showClinicianTreatments',
        'enableMultipleSelection',
      ]),
      multiSelectedTreatments: contextDefaultProps.multiSelectedTreatments,
    }));

  const setAlertMessage = newAlert => {
    setState({ ...state, alertMessage: newAlert });
  };

  const stopLiveTrackingInterval = () => clearInterval(liveTrackingIntervalFunc);

  const startLiveTrackingInterval = (tickFunc, cancelOverrideFlag = true) => {
    if (!isLiveTracking || !isTodaysDate(selectedDate)) {
      stopLiveTrackingInterval();
      return;
    }

    if (cancelOverrideFlag && state.flags.showClinicianTreatments) {
      stopLiveTrackingInterval();
    } else {
      const intervalFuncTicks = tickFunc || state.liveTrackingTick;
      stopLiveTrackingInterval();
      liveTrackingIntervalFunc = setInterval(intervalFuncTicks, refreshTimeInSeconds * 1000);

      setState(newState => ({
        ...newState,
        liveTrackingTick: intervalFuncTicks,
      }));
    }
  };

  const setActiveNonPatientAssignment = assignment => {
    setState({ ...state, activeNonPatientAssignment: assignment });
  };

  const resetTreatmentClickEvent = () =>
    setState(newState => ({
      ...newState,
      availableTimeSpans: contextDefaultProps.availableTimeSpans,
      flags: handleFlags('isEditMode', false),
    }));

  const closeCopyPanels = () => {
    setState({
      ...state,
      activeAssignment: null,
      activeTreatmentInstanceForCopy: null,
      activeClinician: { ...state.activeClinician, activeUnassignedTreatment: null, activeTreatment: null },
    });
  };
  const resetMultiSelectedTreatments = () => {
    setState({
      ...state,
      flags: handleFlags('enableMultipleSelection', false),
      multiSelectedTreatments: contextDefaultProps.multiSelectedTreatments,
    });
  };

  const setActiveNonPatientAssignmentForReschedule = assignment => {
    setState({
      ...state,
      activeNonPatientAssignmentForReschedule: assignment,
    });
  };

  const shouldResetCancelTreatmentInstance = value => {
    setState({
      ...state,
      flags: handleFlags('shouldResetCancelTreatmentInstance', value),
    });
  };

  return {
    resetTreatmentClickEvent,
    startLiveTrackingInterval,
    stopLiveTrackingInterval,
    resetContext,
    resetContextAndModify,
    setAvailableTimeSpans,
    toggleEditMode,
    getAvailableTimeSpans,
    getAccessToken: state.auth,
    createRequestModel,
    getParamsForAssignment,
    getFlag: flagProp => state.flags[flagProp],
    toggleFlag,
    setFlag,
    setFlagAndClinician,
    activeClinicianId: state.activeClinician.id,
    activeClinicianPostingId: state.activeClinician.postingId,
    setActiveClinician,
    activeClinicianTreatments: state.activeClinician.treatments ? state.activeClinician.treatments : [],
    setActiveClinicianTreatments,
    activeAssignment: state.activeAssignment,
    setActiveAssignment,
    activeTreatment,
    setActiveTreatment,
    activeUnassignedTreatment,
    setActiveUnassignedTreatment,
    hasAvailableTimeSpansForClinicianId,
    activeTreatmentTimeSpans: hasActiveTreatmentAvailableTimeSpans() ? activeTreatment.AvailableTimeSpans : [],
    getAvailableTimeSpansForTreatment,
    getAssignmentForEdit: state.assignmentForEdit,
    activeClinician: state.activeClinician,
    activeTreatmentInstanceForCopy: state.activeTreatmentInstanceForCopy,
    setActiveTreatmentInstanceForCopy,
    hasActiveTreatmentAvailableTimeSpans,
    alertMessage: state.alertMessage,
    setAlertMessage,
    activeUnassignedTreatmentForCancel: state.activeUnassignedTreatmentForCancel,
    setActiveUnassignedTreatmentForCancel,
    setActiveUnassignedTreatmentForReschedule,
    setActiveUnassignedTreatmentForChangeUnit,
    activeUnassignedTreatmentForChangeUnit: state.activeUnassignedTreatmentForChangeUnit,
    activeUnassignedTreatmentForReschedule: state.activeUnassignedTreatmentForReschedule,
    refreshClinicianTreatmentsAndClosePanels,
    activeNonPatientAssignment: state.activeNonPatientAssignment,
    setActiveNonPatientAssignment,
    getStatePropValue: prop => (state[prop] !== undefined ? state[prop] : null),
    setStateProp: (prop, value) =>
      state[prop] !== undefined ? setState(newState => ({ ...newState, [prop]: value })) : null,
    closeCopyPanels,
    getMultiSelectedTreatments: state.multiSelectedTreatments,
    setMultiSelectedTreatments: multiSelectedTreatments =>
      setState({
        ...state,
        multiSelectedTreatments,
      }),
    resetMultiSelectedTreatments,
    setActiveNonPatientAssignmentForReschedule,
    activeNonPatientAssignmentForReschedule: state.activeNonPatientAssignmentForReschedule,
    shouldResetCancelTreatmentInstance,
  };
};

export default useTimeline;
